import { inject } from "fw";
import { createFrom, createFromArray } from "fw-model";

import { MarketingOutreachArchive, MarketingOutreachSentBase, MarketingOutreachSentResult, MarketingOutreachSentStatusCounts } from "models/monitor-email";
import { Manager } from "network/manager";
import { DEFAULT_EMAIL_RECIPIENT_PAGE_SIZE, DEFAULT_OUTREACH_SENT_PAGE_SIZE } from "views/monitor/email/overview-constants";

@inject
export class MarketingRepository {
    constructor(private s: Manager) {}

    public async marketingOutreachDeliveryList({
        outreachSentId = "",
        sort = "Id DESC",
        page = 1,
        limit = DEFAULT_EMAIL_RECIPIENT_PAGE_SIZE,
        signal
    }: {
        outreachSentId?: string;
        sort?: string;
        page?: number;
        limit?: number;
        signal?: AbortSignal;
    }): Promise<MarketingOutreachArchive[]> {
        const res = await this.s.getWithSignal<any>(
            "marketing/outreach/delivery_list",
            {
                q: outreachSentId,
                sort,
                page,
                limit
            },
            signal
        );
        return createFromArray(MarketingOutreachArchive, res?.body?.results || []);
    }

    public async marketingOutreachSents({
        atsOrgIds = [],
        includeCampaign = false,
        includeFailedDeliveries = false,
        isDirect = null,
        isOutreach = null,
        isTest = false,
        limit = DEFAULT_OUTREACH_SENT_PAGE_SIZE,
        mediaTypes = "email",
        page = 1,
        recordTypes = "applications,contacts",
        search = "",
        sentAtFrom = "",
        sentAtTo = "",
        sort = "SentAt DESC",
        source = "",
        status = "",
        timezone = "",
        signal
    }: {
        atsOrgIds?: string[];
        includeCampaign?: boolean;
        includeFailedDeliveries?: boolean;
        isDirect?: boolean;
        isOutreach?: boolean;
        isTest?: boolean;
        limit?: number;
        mediaTypes?: string;
        page?: number;
        recordTypes?: string;
        search?: string;
        sentAtFrom?: string;
        sentAtTo?: string;
        sort?: string;
        source?: string;
        status?: string;
        timezone?: string;
        signal?: AbortSignal;
    }): Promise<MarketingOutreachSentResult> {
        const res = await this.s.postWithSignal<any>(
            "marketing/outreach_sents",
            {
                atsOrgIds,
                includeCampaign,
                includeFailedDeliveries,
                isDirect,
                isOutreach,
                isTest,
                limit,
                mediaTypes,
                page,
                recordTypes,
                search,
                sentAtFrom,
                sentAtTo,
                sort,
                source,
                status,
                timezone
            },
            undefined,
            signal
        );

        const outreachSents: MarketingOutreachSentBase[] =
            res.body?.Results?.map((os) => {
                // transform JSON strings into objects
                return {
                    ...os,
                    Stats: JSON.parse(os.Stats as string),
                    EntitySelection: JSON.parse(os.EntitySelection as string)
                };
            }) || [];
        const total: number = parseInt(res.headers["x-manager-total"], 10);

        return {
            list: createFromArray(MarketingOutreachSentBase, outreachSents),
            total
        };
    }

    public async marketingOutreachSentById(id: string, signal?: AbortSignal): Promise<MarketingOutreachSentBase> {
        const res = await this.s.getWithSignal<any>(`marketing/outreach_sents/${id}`, undefined, signal);
        const outreachSent: MarketingOutreachSentBase = res?.body || {};

        // transform JSON strings into objects
        try {
            if (outreachSent?.EntitySelection) {
                outreachSent.EntitySelection = JSON.parse((outreachSent?.EntitySelection || "") as string);
            }
            if (outreachSent?.Stats) {
                outreachSent.Stats = JSON.parse((outreachSent?.Stats || "") as string);
            }
        } catch (error) {
            console.log(error);
        }

        return createFrom(MarketingOutreachSentBase, outreachSent);
    }

    public async marketingOutreachSentsStatusCounts({
        atsOrgIds = [],
        isDirect = null,
        isOutreach = null,
        isTest = false,
        mediaTypes = "email",
        recordTypes = "applications,contacts",
        search = "",
        sentAtFrom = "",
        sentAtTo = "",
        source = "",
        timezone = "",
        signal
    }: {
        atsOrgIds?: string[];
        isDirect?: boolean;
        isOutreach?: boolean;
        isTest?: boolean;
        mediaTypes?: string;
        recordTypes?: string;
        search?: string;
        sentAtFrom?: string;
        sentAtTo?: string;
        source?: string;
        timezone?: string;
        signal?: AbortSignal;
    }): Promise<MarketingOutreachSentStatusCounts> {
        const res = await this.s.postWithSignal<any>(
            "marketing/outreach_sents/status_counts",
            {
                atsOrgIds,
                isDirect,
                isOutreach,
                isTest,
                mediaTypes,
                recordTypes,
                search,
                sentAtFrom,
                sentAtTo,
                source,
                timezone
            },
            undefined,
            signal
        );
        return createFrom(MarketingOutreachSentStatusCounts, res?.body?.status || {});
    }
}
